/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file moduleNavs.js
 * @description
 * Single source of truth for the **module-scoped bottom navigation** — the
 * dynamic bottom bar that replaces the app's main tab bar while the user is
 * inside a module. Every module belongs to a *group* (mirroring the admin
 * sidebar sections) whose tabs are the module's real, navigable destinations —
 * no placeholder tabs, only tabs backed by a live screen / list / view.
 *
 * A group tab declares exactly one action:
 *   - `list`   → open {@link module:screens/ManagementListScreen} for that `type`
 *   - `screen` → open a stack route (registered in AppNavigator)
 *   - `view`   → switch an in-screen view (the screen wires it via `onView`)
 *
 * Consumed by {@link module:helpers/useModuleNav} (turns a group + active tab
 * into ready-to-render {@link module:components/module/ModuleTabBar} props) and,
 * for list-backed modules, {@link module:screens/ManagementListScreen} (which
 * resolves its owning group from the route `type` via {@link LIST_TYPE_TO_MODULE}).
 *
 * Labels use short `mtab_*` i18n keys (kept in the mail locale bundle, merged
 * app-wide) with a Hungarian fallback so the bar is never blank.
 */

import {isFeatureEnabled, isScreenEnabled} from './featureFlags';

/**
 * @typedef {object} ModuleTab
 * @property {string} key       Stable tab id (also the `activeTab` a screen reports).
 * @property {string} labelKey  Short i18n key for the tab label.
 * @property {string} fallback  Hungarian fallback label.
 * @property {string} icon      MaterialCommunityIcons glyph name.
 * @property {string} [list]    ManagementListScreen `type` to open (list action).
 * @property {string} [screen]  Stack route name to open (screen action).
 * @property {string} [view]    In-screen view key (view action; needs `onView`).
 */

/**
 * @typedef {object} ModuleGroup
 * @property {string} icon        Representative glyph (for the group as a whole).
 * @property {ModuleTab[]} tabs   The group's bottom tabs (kept to ≤4 for phones).
 */

/** @type {Object.<string, ModuleGroup>} */
export const MODULE_GROUPS = {
    // Sales — day-to-day selling (admin: Értékesítés).
    sales: {
        icon: 'storefront-outline',
        tabs: [
            {key: 'orders', labelKey: 'mtab_orders', fallback: 'Rendelések', icon: 'clipboard-list-outline', list: 'orders'},
            {key: 'products', labelKey: 'mtab_products', fallback: 'Termékek', icon: 'food-fork-drink', list: 'products'},
            {key: 'customers', labelKey: 'mtab_customers', fallback: 'Ügyfelek', icon: 'account-heart-outline', list: 'customers'},
        ],
    },
    // Finance cockpit.
    finance: {
        icon: 'cash-multiple',
        tabs: [
            {key: 'overview', labelKey: 'mtab_overview', fallback: 'Áttekintés', icon: 'view-dashboard-outline', screen: 'ManagementFinanceScreen'},
            {key: 'inbound', labelKey: 'mtab_inbound', fallback: 'Bejövő', icon: 'file-import-outline', list: 'invoicesInbound'},
            {key: 'outbound', labelKey: 'mtab_outbound', fallback: 'Kimenő', icon: 'file-export-outline', list: 'invoicesOutbound'},
            {key: 'banks', labelKey: 'mtab_banks', fallback: 'Bankok', icon: 'bank-outline', list: 'banks'},
        ],
    },
    // Field service.
    service: {
        icon: 'tools',
        tabs: [
            {key: 'overview', labelKey: 'mtab_overview', fallback: 'Áttekintés', icon: 'view-dashboard-outline', screen: 'ManagementServiceScreen'},
            {key: 'workOrders', labelKey: 'mtab_workorders', fallback: 'Munkalapok', icon: 'clipboard-text-outline', list: 'workOrders'},
            {key: 'appointments', labelKey: 'mtab_appointments', fallback: 'Időpontok', icon: 'calendar-clock', list: 'appointments'},
            {key: 'technicians', labelKey: 'mtab_technicians', fallback: 'Szakik', icon: 'account-wrench-outline', list: 'technicians'},
        ],
    },
    // CRM.
    //
    // APOLLO IS DELIBERATELY NOT A TAB HERE. It was specified as a fifth tab,
    // and it was measured instead of assumed:
    // {@link module:components/module/ModuleTabBar} lays its tabs out as
    // `flex: 1` cells beside a fixed exit pill, and both its file header and the
    // {@link ModuleGroup} typedef above cap a group at FOUR tabs on a phone. On a
    // 360dp device the bar is 340dp wide; after 12dp padding, the 44dp compact
    // exit pill (340 < `EXIT_LABEL_MIN_WIDTH`) and the 11dp divider, 273dp remain
    // — ~68dp per cell at four tabs, but only ~54dp at five, of which ~50dp is
    // usable label width for a 10.5px single-line label. "Áttekintés",
    // "Ügyfelek" and "Ajánlatok" would all ellipsize, i.e. adding Apollo here
    // degrades the FOUR destinations that already work.
    //
    // Dropping `quotes` to make room was rejected outright: that removes a live,
    // working destination to make space for a new one. Apollo is therefore
    // reached from the CRM overview instead (a ModuleCard on
    // {@link module:screens/ManagementCrmScreen}, which IS this group's
    // `overview` tab), from the Modules hub tile
    // ({@link module:config/managementModules}) and, in the Sales app mode, from
    // the "Egyéb" hub row — three proven entry points, none of which costs the
    // bar a tab. Revisit only if the bar learns to scroll or wrap.
    crm: {
        icon: 'account-tie-outline',
        tabs: [
            {key: 'overview', labelKey: 'mtab_overview', fallback: 'Áttekintés', icon: 'view-dashboard-outline', screen: 'ManagementCrmScreen'},
            {key: 'leads', labelKey: 'mtab_leads', fallback: 'Leadek', icon: 'account-plus-outline', list: 'leads'},
            {key: 'customers', labelKey: 'mtab_customers', fallback: 'Ügyfelek', icon: 'account-heart-outline', list: 'customers'},
            {key: 'quotes', labelKey: 'mtab_quotes', fallback: 'Ajánlatok', icon: 'file-document-outline', list: 'quotes'},
        ],
    },
    // Sales module (app mode `sales`, the CRM-driven field-sales app).
    //
    // NOT to be confused with the `sales` group above: that one is the
    // Management sub-bar of the admin "Értékesítés" section (orders / products /
    // customers) and predates this module. The key `sales` was already taken, so
    // the field-sales module owns `salesModule` — renaming the older group would
    // silently break every `moduleGroup: 'sales'` route param already in flight.
    //
    // The bar only ever renders when one of these lists is opened STANDALONE:
    // inside the Sales tab navigator every list is opened with
    // `hideModuleBar: true` (see screens/SalesHomeScreen.js), which resolves the
    // group to null so a second bar never stacks under the tab bar.
    salesModule: {
        icon: 'briefcase-account-outline',
        tabs: [
            {key: 'overview', labelKey: 'mtab_overview', fallback: 'Áttekintés', icon: 'view-dashboard-outline', screen: 'SalesDashboardScreen'},
            {key: 'leads', labelKey: 'mtab_leads', fallback: 'Leadek', icon: 'account-plus-outline', list: 'leads'},
            {key: 'customers', labelKey: 'mtab_customers', fallback: 'Ügyfelek', icon: 'account-heart-outline', list: 'unifiedCustomers'},
            {key: 'orders', labelKey: 'mtab_orders', fallback: 'Rendelések', icon: 'clipboard-list-outline', screen: 'SalesOrdersScreen'},
        ],
    },
    // Procurement (Beszerzés) — the PR → RFQ → PO cycle.
    //
    // Four tabs exactly, per the cap documented on {@link ModuleGroup} above:
    // the overview plus the module's three document types. The planner
    // (ProcurementSourcingScreen), the reorder rules and the settings are
    // reached from the overview's module cards instead of a fifth/sixth tab —
    // adding them would ellipsize the four labels that already work, and they
    // are destinations a buyer opens occasionally, not all day.
    procurement: {
        icon: 'cart-outline',
        tabs: [
            {key: 'overview', labelKey: 'mtab_overview', fallback: 'Áttekintés', icon: 'view-dashboard-outline', screen: 'ProcurementHomeScreen'},
            {key: 'requisitions', labelKey: 'mtab_requisitions', fallback: 'Igénylések', icon: 'playlist-check', screen: 'ProcurementRequisitionsScreen'},
            {key: 'rfqs', labelKey: 'mtab_rfqs', fallback: 'Ajánlatok', icon: 'tag-multiple-outline', screen: 'ProcurementRfqsScreen'},
            {key: 'orders', labelKey: 'mtab_purchase_orders', fallback: 'Rendelés', icon: 'cart-outline', screen: 'ProcurementOrdersScreen'},
        ],
    },
    // Utility bills & meters (Rezsi nyilvántartás) — four tabs: overview, the
    // provider list (detail + bill entry hang off it), the meter-reading entry
    // (the field use case) and the anomalies.
    utilities: {
        icon: 'flash-outline',
        tabs: [
            {key: 'overview', labelKey: 'mtab_overview', fallback: 'Áttekintés', icon: 'view-dashboard-outline', screen: 'UtilityHomeScreen'},
            {key: 'providers', labelKey: 'mtab_utility_providers', fallback: 'Szolgáltatók', icon: 'office-building-outline', screen: 'UtilityProvidersScreen'},
            {key: 'reading', labelKey: 'mtab_utility_reading', fallback: 'Leolvasás', icon: 'gauge', screen: 'UtilityReadingScreen'},
            {key: 'anomalies', labelKey: 'mtab_utility_anomalies', fallback: 'Eltérések', icon: 'alert-decagram-outline', screen: 'UtilityAnomaliesScreen'},
        ],
    },
    // Fleet (Flotta) — fuel statistics, the refuel list with GPS evidence and the
    // driver break statistics.
    fleet: {
        icon: 'car-outline',
        tabs: [
            {key: 'stats', labelKey: 'mtab_fleet_stats', fallback: 'Statisztika', icon: 'chart-areaspline', screen: 'ManagementFleetStatsScreen'},
            {key: 'refuels', labelKey: 'mtab_fleet_refuels', fallback: 'Tankolások', icon: 'gas-station-outline', screen: 'ManagementRefuelsScreen'},
            {key: 'breaks', labelKey: 'mtab_fleet_breaks', fallback: 'Pihenők', icon: 'coffee-outline', screen: 'ManagementDriverBreaksScreen'},
        ],
    },
    // Projects (admin: Projektek) — project list and the task list. Boards,
    // workspace and reports stay in the admin web.
    projects: {
        icon: 'briefcase-outline',
        tabs: [
            {key: 'projects', labelKey: 'mtab_projects', fallback: 'Projektek', icon: 'briefcase-outline', list: 'projects'},
            {key: 'tasks', labelKey: 'mtab_tasks', fallback: 'Feladatok', icon: 'checkbox-marked-circle-outline', list: 'tasks'},
        ],
    },
    // Customer support (admin: Ügyfélszolgálat) — tickets and complaints.
    support: {
        icon: 'lifebuoy',
        tabs: [
            {key: 'tickets', labelKey: 'mtab_support_tickets', fallback: 'Hibajegyek', icon: 'lifebuoy', list: 'supportTickets'},
            {key: 'complaints', labelKey: 'mtab_complaints', fallback: 'Panaszok', icon: 'alert-octagon-outline', list: 'complaints'},
        ],
    },
    // Logistics / stock.
    logistics: {
        icon: 'truck-fast-outline',
        tabs: [
            {key: 'overview', labelKey: 'mtab_overview', fallback: 'Áttekintés', icon: 'view-dashboard-outline', screen: 'ManagementLogisticsScreen'},
            {key: 'stock', labelKey: 'mtab_stock', fallback: 'Készlet', icon: 'warehouse', list: 'stock'},
            {key: 'warehouses', labelKey: 'mtab_warehouses', fallback: 'Raktárak', icon: 'home-group', list: 'warehouses'},
            {key: 'transactions', labelKey: 'mtab_transactions', fallback: 'Mozgások', icon: 'swap-horizontal', list: 'transactions'},
        ],
    },
    // Team & access.
    team: {
        icon: 'account-group-outline',
        tabs: [
            {key: 'users', labelKey: 'mtab_users', fallback: 'Felhasználók', icon: 'account-group-outline', screen: 'ManagementUsersScreen'},
            {key: 'roles', labelKey: 'mtab_roles', fallback: 'Szerepkörök', icon: 'shield-account-outline', screen: 'ManagementRolesScreen'},
            {key: 'templates', labelKey: 'mtab_templates', fallback: 'Sablonok', icon: 'widgets-outline', screen: 'ManagementModuleTemplatesScreen'},
        ],
    },
    // General & system tooling.
    system: {
        icon: 'cog-outline',
        tabs: [
            {key: 'store', labelKey: 'mtab_store', fallback: 'Üzlet', icon: 'storefront-outline', screen: 'ManagementStoreSettingsScreen'},
            {key: 'billing', labelKey: 'mtab_billing', fallback: 'Számlázás', icon: 'credit-card-outline', screen: 'ManagementBillingScreen'},
            {key: 'automations', labelKey: 'mtab_automations', fallback: 'Automatizmus', icon: 'robot-outline', screen: 'ManagementAutomationsScreen'},
            {key: 'deployments', labelKey: 'mtab_deployments', fallback: 'Telepítés', icon: 'rocket-launch-outline', screen: 'ManagementDeploymentsScreen'},
        ],
    },
    // Mail — the in-app mail client. Tabs are in-screen views (folder/filter
    // switches) handled by MailHomeScreen via `onView`; see the mail folder map.
    mail: {
        icon: 'email-outline',
        tabs: [
            {key: 'inbox', labelKey: 'mtab_inbox', fallback: 'Beérkezett', icon: 'inbox', view: 'inbox'},
            {key: 'sent', labelKey: 'mtab_sent', fallback: 'Elküldött', icon: 'send-outline', view: 'sent'},
            {key: 'drafts', labelKey: 'mtab_drafts', fallback: 'Piszkozatok', icon: 'file-document-edit-outline', view: 'drafts'},
            {key: 'archive', labelKey: 'mtab_archive', fallback: 'Archívum', icon: 'archive-outline', view: 'archive'},
        ],
    },
};

/**
 * Default `{group, tab}` for a ManagementListScreen `type`, so a list opened
 * directly from a hub tile still shows the correct module bar with the right
 * tab highlighted. Ambiguous types (e.g. `customers`, in both Sales & CRM)
 * default to their primary group; an explicit `moduleGroup`/`moduleTab` route
 * param always wins over this map.
 * @type {Object.<string, {group:string, tab:string}>}
 */
export const LIST_TYPE_TO_MODULE = {
    orders: {group: 'sales', tab: 'orders'},
    products: {group: 'sales', tab: 'products'},
    customers: {group: 'sales', tab: 'customers'},
    invoicesInbound: {group: 'finance', tab: 'inbound'},
    invoicesOutbound: {group: 'finance', tab: 'outbound'},
    banks: {group: 'finance', tab: 'banks'},
    registers: {group: 'finance', tab: 'overview'},
    costTypes: {group: 'finance', tab: 'overview'},
    expenses: {group: 'finance', tab: 'overview'},
    workOrders: {group: 'service', tab: 'workOrders'},
    appointments: {group: 'service', tab: 'appointments'},
    technicians: {group: 'service', tab: 'technicians'},
    leads: {group: 'crm', tab: 'leads'},
    quotes: {group: 'crm', tab: 'quotes'},
    // The unified customer index is a Sales-module surface only — the CRM hub
    // opens the legacy `customers` list, so this mapping cannot change what a
    // management build renders today (nothing navigates there without the Sales
    // tab's `hideModuleBar: true`).
    unifiedCustomers: {group: 'salesModule', tab: 'customers'},
    stock: {group: 'logistics', tab: 'stock'},
    warehouses: {group: 'logistics', tab: 'warehouses'},
    transactions: {group: 'logistics', tab: 'transactions'},
    integrations: {group: 'logistics', tab: 'overview'},
    users: {group: 'team', tab: 'users'},
    projects: {group: 'projects', tab: 'projects'},
    tasks: {group: 'projects', tab: 'tasks'},
    supportTickets: {group: 'support', tab: 'tickets'},
    complaints: {group: 'support', tab: 'complaints'},
};

/**
 * Resolve the group definition for a group id, narrowed by the build-time
 * switchboard ({@link module:config/featureFlags}).
 *
 * Three levels of granularity, all optional — anything not listed in
 * `featureFlags.js` stays visible:
 *   - `moduleNav.<group>`           hides the whole bottom bar for that module
 *   - `moduleNav.<group>.<tabKey>`  hides one tab (e.g. `moduleNav.finance.banks`)
 *   - `screen.<RouteName>`          hides every tab that opens that route
 *
 * A group whose tabs are all filtered out resolves to `null`, so
 * {@link module:helpers/useModuleNav} renders no bar instead of an empty one.
 *
 * @param {string} group Module group id (a key of {@link MODULE_GROUPS}).
 * @returns {ModuleGroup|null} The group with only its enabled tabs, or null.
 */
export const getModuleGroup = (group) => {
    if (!group || !MODULE_GROUPS[group] || !isFeatureEnabled(`moduleNav.${group}`)) {
        return null;
    }

    const def = MODULE_GROUPS[group];
    const tabs = def.tabs.filter((tab) => isFeatureEnabled(`moduleNav.${group}.${tab.key}`)
        && (!tab.screen || isScreenEnabled(tab.screen)));

    // Identity-preserving fast path: with nothing filtered (the `full` build,
    // and every group in most trimmed builds) callers get the exact same object
    // back, so no allocation per render and `===` comparisons still hold.
    if (tabs.length === def.tabs.length) {
        return def;
    }

    return tabs.length ? {...def, tabs} : null;
};

/**
 * Resolve the owning module {group, tab} for a ManagementListScreen `type`,
 * preferring explicit route params over the {@link LIST_TYPE_TO_MODULE} default.
 *
 * `hideModuleBar` wins over both: a list rendered INSIDE a tab (the Sales module
 * embeds the generic list screen in one of its tabs) must not draw a second
 * module bar underneath the tab bar, so it resolves to `null` and
 * {@link module:helpers/useModuleNav} renders nothing.
 *
 * @param {string} type              The list `type`.
 * @param {object} [params]          Route params.
 * @param {string} [params.moduleGroup]
 * @param {string} [params.moduleTab]
 * @param {boolean} [params.hideModuleBar] Suppress the module bar entirely.
 * @returns {{group:string, tab:string}|null}
 */
export const resolveListModule = (type, {moduleGroup, moduleTab, hideModuleBar} = {}) => {
    if (hideModuleBar) {
        return null;
    }

    if (moduleGroup) {
        return {group: moduleGroup, tab: moduleTab || LIST_TYPE_TO_MODULE[type]?.tab || ''};
    }

    return LIST_TYPE_TO_MODULE[type] || null;
};

export default MODULE_GROUPS;
